// Package Imports
import { z } from 'zod'

// Module Imports
import {
  MAX_CONFIDENCE_THRESHOLD,
  MAX_GENERATION_COUNT,
  MIN_CONFIDENCE_THRESHOLD,
  MIN_GENERATION_COUNT,
} from '@/modules/question-generation/question-generation.constants'

const errors = {
  name: {
    required: 'Üretim grubu adı girilmesi zorunludur',
    max: 'Üretim grubu adı en fazla 120 karakter olmalıdır',
  },
  topic: {
    required: 'Konu detayı girilmesi zorunludur',
    max: 'Konu detayı en fazla 500 karakter olmalıdır',
  },
  count: {
    min: `En az ${MIN_GENERATION_COUNT} soru üretilmelidir`,
    max: `Tek seferde en fazla ${MAX_GENERATION_COUNT} soru üretilebilir`,
  },
  category: {
    required: 'Kategori seçilmesi zorunludur',
  },
  difficulty: {
    required: 'Zorluk seçilmesi zorunludur',
  },
  match_modes: {
    min: 'Oyun klasmanı seçilmesi zorunludur',
  },
  verification_rigor: {
    fastWithAutoApprove:
      'Otomatik onay açıkken doğrulama titizliği en az "Dengeli" olmalıdır',
  },
}

export const batchSearchSchema = z.object({
  page: z.number().optional(),
  query: z.string().optional(),
  // Köken filtresi (Hepsi/Taslak/Özel/Periyodik). Yoksa hepsi.
  kind: z.enum(['ALL', 'DRAFT', 'INDIVIDUAL', 'PERIODIC']).optional(),
})

/** Yeni üretim ekranı arama paramları. `from` → "Kopyala": kaynak grubun id'si
 * (ayarları forma önceden doldurmak için detay çekilir). */
export const newGenerationSearchSchema = z.object({
  from: z.string().optional(),
})

export const generationCriteriaSchema = z
  .object({
    name: z.string().min(1, errors.name.required).max(120, errors.name.max),
    topic: z.string().min(1, errors.topic.required).max(500, errors.topic.max),
    count: z
      .number({ message: errors.count.min })
      .int()
      .min(MIN_GENERATION_COUNT, errors.count.min)
      .max(MAX_GENERATION_COUNT, errors.count.max),
    category_id: z.string().min(1, errors.category.required),
    // Kategorinin doğrudan çocuğu — her zaman opsiyonel (Bilgilig sözleşmesi).
    subcategory_id: z.string().optional(),
    // Kategori ağacından bağımsız, opsiyonel Bilgilig soru havuzu.
    question_pool_id: z.string().optional(),
    difficulty: z.string().min(1, errors.difficulty.required),
    match_modes: z.array(z.string()).min(1, errors.match_modes.min),
    language_tone: z.string().optional(),
    extra_instructions: z.string().max(1000).optional(),
    provider: z.enum(['ollama', 'openrouter']),
    // Dropdown'dan seçilen somut OpenRouter modeli (ör. "google/gemini-2.5-flash").
    // Ollama'da yok sayılır; boş gönderilirse backend varsayılana düşer.
    model: z.string(),
    web_search: z.boolean(),
    // --- Otomatik AI doğrulama/onay (üretim sonrası) — her aksiyon bağımsız. ---
    auto_approve_enabled: z.boolean(),
    approve_confidence_threshold: z
      .number()
      .int()
      .min(MIN_CONFIDENCE_THRESHOLD)
      .max(MAX_CONFIDENCE_THRESHOLD),
    auto_revise_enabled: z.boolean(),
    revise_max_attempts: z.number().int().min(1).max(3),
    auto_reject_enabled: z.boolean(),
    reject_confidence_threshold: z
      .number()
      .int()
      .min(MIN_CONFIDENCE_THRESHOLD)
      .max(MAX_CONFIDENCE_THRESHOLD),
    // Doğrulamada web aramasıyla olgusal teyit (judge OpenRouter web_search kullanır).
    verify_with_web: z.boolean(),
    // Doğrulama (judge) titizliği — judge model katmanı + oy sayısını ayarlar (ham model değil).
    verification_rigor: z.enum(['FAST', 'BALANCED', 'THOROUGH']),
  })
  // Guardrail: auto-onay en riskli karar (docs/pipeline.md) → zayıf judge ("Hızlı") ile onaya
  // izin verilmez. Diğer aksiyonlar (reddet/revize) için her titizlik serbest.
  .refine(
    (data) =>
      !(data.auto_approve_enabled && data.verification_rigor === 'FAST'),
    {
      message: errors.verification_rigor.fastWithAutoApprove,
      path: ['verification_rigor'],
    }
  )
